import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MessageSquare, Trash2, Image as ImageIcon, Send } from 'lucide-react';
import { collection, addDoc, onSnapshot, query, orderBy, limit, updateDoc, doc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { K } from '../constants';
import { useToast } from '../components/Toast';
import { compressImage } from '../utils/imageUpload';
import { fsId } from '../utils/formulas';

const REACTIONS = ['👍', '❤️', '😂', '🔥', '👀', '💯'];
const MSG_LIMIT = 100;

function emailToName(email) {
  if (!email) return 'Unknown';
  return email.split('@')[0];
}

function userName(u) {
  return u?.displayName || emailToName(u?.email);
}

function timeAgo(ts) {
  if (!ts) return '';
  const now = Date.now();
  const then = ts.toDate ? ts.toDate().getTime() : ts;
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const d = new Date(then);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function MentionInput({ value, onChange, onSubmit, users, placeholder }) {
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionActive, setMentionActive] = useState(false);
  const [mentionIdx, setMentionIdx] = useState(0);
  const [cursorPos, setCursorPos] = useState(0);
  const inputRef = useRef(null);

  const mentionResults = useMemo(() => {
    if (!mentionActive || !mentionQuery) return [];
    const q = mentionQuery.toLowerCase();
    return users.filter((u) => userName(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q)).slice(0, 5);
  }, [mentionActive, mentionQuery, users]);

  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setCursorPos(pos);
    onChange(val);

    // Check if we're in a mention context
    const textBefore = val.slice(0, pos);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionActive(true);
      setMentionQuery(atMatch[1]);
      setMentionIdx(0);
    } else {
      setMentionActive(false);
      setMentionQuery('');
    }
  };

  const insertMention = (user) => {
    const textBefore = value.slice(0, cursorPos);
    const textAfter = value.slice(cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    const newText = textBefore.slice(0, atIdx) + `@${userName(user)} ` + textAfter;
    onChange(newText);
    setMentionActive(false);
    setMentionQuery('');
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (mentionActive && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIdx((i) => Math.min(i + 1, mentionResults.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionResults[mentionIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionActive(false);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !mentionActive) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div style={{ position: 'relative', flex: 1 }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        style={{
          width: '100%',
          background: K.bg2,
          border: `1px solid ${K.bdr}`,
          borderRadius: 8,
          padding: '10px 12px',
          color: K.t0,
          fontSize: 13,
          fontFamily: K.fSans,
          outline: 'none',
        }}
      />
      {mentionActive && mentionResults.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 0, right: 0,
          background: K.bg1, border: `1px solid ${K.bdr2}`, borderRadius: 8,
          marginBottom: 4, overflow: 'hidden', zIndex: 10,
          boxShadow: '0 -4px 16px #00000044',
        }}>
          {mentionResults.map((u, i) => (
            <button
              key={u.id}
              onClick={() => insertMention(u)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', background: i === mentionIdx ? K.bg3 : 'transparent',
                border: 'none', color: K.t0, cursor: 'pointer', textAlign: 'left',
                fontFamily: K.fSans, fontSize: 12,
              }}
            >
              {u.photoUrl ? (
                <img src={u.photoUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', background: K.info,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                }}>
                  {userName(u).charAt(0).toUpperCase()}
                </span>
              )}
              <span>{userName(u)}</span>
              <span style={{ color: K.t3, fontSize: 10, marginLeft: 'auto' }}>{u.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MessageBubble({ msg, currentUid, users, onReact, onDelete, isAdmin }) {
  const [showReactions, setShowReactions] = useState(false);
  const isMine = msg.uid === currentUid;

  const renderText = (text) => {
    if (!text) return null;
    // Split on @mentions and highlight them
    const parts = text.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return (
          <span key={i} style={{ color: K.info, fontWeight: 700 }}>{part}</span>
        );
      }
      return part;
    });
  };

  const reactions = msg.reactions || {};
  const reactionEntries = Object.entries(reactions).filter(([, uids]) => uids && uids.length > 0);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      alignItems: isMine ? 'flex-end' : 'flex-start',
      marginBottom: 6,
    }}>
      {!isMine && (() => {
        const sender = users.find((u) => u.id === msg.uid);
        return (
          <div style={{ fontSize: 10, color: K.t3, marginBottom: 2, marginLeft: 4, fontFamily: K.fSans }}>
            {sender?.displayName || emailToName(msg.email)}
          </div>
        );
      })()}
      <div
        onClick={() => setShowReactions(!showReactions)}
        style={{
          background: isMine ? `${K.info}22` : K.bg2,
          border: `1px solid ${isMine ? `${K.info}44` : K.bdr}`,
          borderRadius: isMine ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          padding: msg.imageUrl ? 4 : '8px 14px',
          maxWidth: '80%',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        {msg.imageUrl && (
          <img
            src={msg.imageUrl}
            alt="shared"
            style={{
              maxWidth: '100%', maxHeight: 240, borderRadius: 10,
              display: 'block', marginBottom: msg.text ? 6 : 0,
            }}
          />
        )}
        {msg.text && (
          <div style={{ fontSize: 13, color: K.t0, lineHeight: 1.5, wordBreak: 'break-word', padding: msg.imageUrl ? '4px 10px 6px' : 0 }}>
            {renderText(msg.text)}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, marginLeft: 4, marginRight: 4 }}>
        <span style={{ fontSize: 9, color: K.t3 }}>{timeAgo(msg.createdAt)}</span>
        {reactionEntries.length > 0 && (
          <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {reactionEntries.map(([emoji, uids]) => {
              const didReact = uids.includes(currentUid);
              return (
                <button
                  key={emoji}
                  onClick={(e) => { e.stopPropagation(); onReact(msg.id, emoji); }}
                  className="hc-btn"
                  style={{
                    background: didReact ? `${K.info}22` : K.bg2,
                    border: `1px solid ${didReact ? K.info : K.bdr}`,
                    borderRadius: 10, padding: '1px 5px', cursor: 'pointer',
                    fontSize: 11, display: 'flex', alignItems: 'center', gap: 2,
                  }}
                >
                  <span>{emoji}</span>
                  <span style={{ fontSize: 9, color: K.t2 }}>{uids.length}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showReactions && (
        <div style={{
          display: 'flex', gap: 4, marginTop: 4,
          background: K.bg1, border: `1px solid ${K.bdr2}`, borderRadius: 20,
          padding: '4px 8px', boxShadow: '0 4px 12px #00000044',
        }}>
          {REACTIONS.map((emoji) => (
            <button
              key={emoji}
              onClick={(e) => { e.stopPropagation(); onReact(msg.id, emoji); setShowReactions(false); }}
              className="hc-btn"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: '2px 4px' }}
            >
              {emoji}
            </button>
          ))}
          {(isMine || isAdmin) && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(msg.id); setShowReactions(false); }}
              className="hc-btn"
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '2px 4px', color: K.bad }}
              title="Delete"
            >
              <Trash2 size={16} color={K.bad} strokeWidth={1.75} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function Chat({ user, isAdmin }) {
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const prevCountRef = useRef(0);
  const toast = useToast();

  // Listen to chat messages
  useEffect(() => {
    const q = query(collection(db, 'chat'), orderBy('createdAt', 'desc'), limit(MSG_LIMIT));
    return onSnapshot(q, (snap) => {
      const msgs = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
      setMessages(msgs);
    });
  }, []);

  // Listen to users for mention autocomplete
  useEffect(() => {
    return onSnapshot(collection(db, 'users'), (snap) => {
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > prevCountRef.current) {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
    prevCountRef.current = messages.length;
  }, [messages.length]);

  const sendMessage = useCallback(async (imageUrl) => {
    const trimmed = text.trim();
    if (!trimmed && !imageUrl) return;
    if (!user) return;
    setSending(true);
    try {
      await addDoc(collection(db, 'chat'), {
        text: trimmed || '',
        uid: user.uid,
        email: user.email,
        imageUrl: imageUrl || null,
        reactions: {},
        createdAt: serverTimestamp(),
      });
      setText('');
    } catch (e) {
      toast('Failed to send message', 'error');
    }
    setSending(false);
  }, [text, user, toast]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      toast('Image must be under 5 MB', 'warn');
      return;
    }
    setUploading(true);
    try {
      // Re-encode to strip EXIF/GPS metadata and shrink before upload.
      // Generated filename avoids putting raw user filenames in the storage path.
      const blob = await compressImage(file);
      const path = `chat/${fsId()}.jpg`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(storageRef);
      await sendMessage(url);
    } catch (err) {
      toast('Image upload failed', 'error');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleReact = async (msgId, emoji) => {
    if (!user) return;
    const msg = messages.find((m) => m.id === msgId);
    if (!msg) return;
    const reactions = { ...(msg.reactions || {}) };
    const uids = reactions[emoji] ? [...reactions[emoji]] : [];
    const idx = uids.indexOf(user.uid);
    if (idx >= 0) {
      uids.splice(idx, 1);
    } else {
      uids.push(user.uid);
    }
    reactions[emoji] = uids;
    await updateDoc(doc(db, 'chat', msgId), { reactions });
  };

  const handleDelete = async (msgId) => {
    await deleteDoc(doc(db, 'chat', msgId));
  };

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}>
          <MessageSquare size={40} color={K.info} strokeWidth={1.75} />
        </div>
        <div style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 700, color: K.t0, marginBottom: 6 }}>Team Chat</div>
        <div style={{ fontSize: 12, color: K.t3 }}>Log in to join the conversation.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)', maxHeight: 700 }}>
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontFamily: K.fSans, fontSize: 26, fontWeight: 700, letterSpacing: 0.2, color: K.t0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MessageSquare size={24} color={K.info} strokeWidth={1.75} />
          Team <span style={{ color: K.info }}>Chat</span>
        </div>
        <div style={{ fontSize: 11, color: K.t3, marginTop: 2 }}>{users.length} members · {messages.length} messages</div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1, overflowY: 'auto', padding: '12px 4px',
          background: K.bg1, border: `1px solid ${K.bdr}`, borderRadius: 12,
          marginBottom: 10,
        }}
      >
        {messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: K.t3, fontSize: 12 }}>
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              msg={msg}
              currentUid={user.uid}
              users={users}
              onReact={handleReact}
              onDelete={handleDelete}
              isAdmin={isAdmin}
            />
          ))
        )}
      </div>

      {/* Input bar */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button
          className="hc-btn"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{
            background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 8,
            padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: K.t1,
            flexShrink: 0,
          }}
          title="Share image"
        >
          <ImageIcon size={18} color={K.t2} strokeWidth={1.75} />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
        <MentionInput
          value={text}
          onChange={setText}
          onSubmit={() => sendMessage()}
          users={users}
          placeholder={uploading ? 'Uploading image...' : 'Type a message... (@ to mention)'}
        />
        <button
          className="hc-btn"
          onClick={() => sendMessage()}
          disabled={sending || (!text.trim())}
          style={{
            background: K.info, border: `1px solid ${K.info}`,
            borderRadius: 8, padding: '10px 14px', cursor: 'pointer',
            display: 'flex', alignItems: 'center', color: '#fff', flexShrink: 0,
            opacity: sending || !text.trim() ? 0.5 : 1,
          }}
        >
          <Send size={18} color="#fff" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
